import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { getLogoWhite } from "@/utils/assets";
import { lovable } from "@/integrations/lovable/index";
import { supabase } from "@/integrations/supabase/client";
import { LanguageProvider, useLang } from "@/i18n/language";
import { portalContent } from "@/i18n/portal";

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Portal del participante | Share The Med" },
      {
        name: "description",
        content:
          "Crea tu cuenta Share The Med para aplicar al programa de mentoría en investigación médica y dar seguimiento a tu solicitud.",
      },
      { property: "og:title", content: "Portal del participante | Share The Med" },
      {
        property: "og:description",
        content: "Accede al portal STM para aplicar al programa y seguir tu solicitud.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <LanguageProvider>
      <AuthPage />
    </LanguageProvider>
  ),
});

const signUpSchema = z.object({
  email: z.string().trim().email().max(255),
  password: z.string().min(8).max(72),
  fullName: z.string().trim().min(2).max(120),
  country: z.string().trim().min(2).max(80),
  medicalArea: z.string().trim().min(2).max(120),
});

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";

function AuthPage() {
  const logo = getLogoWhite();
  const { lang, toggle } = useLang();
  const t = portalContent[lang].auth;
  const navigate = useNavigate();

  const [mode, setMode] = useState<"in" | "up">("in");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    fullName: "",
    country: "",
    medicalArea: "",
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/portal", replace: true });
    });
  }, [navigate]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "up") {
        const parsed = signUpSchema.safeParse(form);
        if (!parsed.success) {
          toast.error(parsed.error.issues[0]?.message ?? "Invalid data");
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email: parsed.data.email,
          password: parsed.data.password,
          options: {
            emailRedirectTo: window.location.origin + "/auth",
            data: {
              full_name: parsed.data.fullName,
              country: parsed.data.country,
              medical_area: parsed.data.medicalArea,
            },
          },
        });
        if (error) {
          toast.error(error.message);
          return;
        }
        if (data.session) navigate({ to: "/portal", replace: true });
        else toast.success(t.checkEmail);
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: form.email.trim(),
          password: form.password,
        });
        if (error) {
          toast.error(error.message);
          return;
        }
        navigate({ to: "/portal", replace: true });
      }
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth",
    });
    if (result.error) {
      toast.error(result.error.message);
      setBusy(false);
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/portal", replace: true });
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-5 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo.url} alt="Share The Med" className="h-9 w-auto" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              {t.backHome}
            </span>
          </Link>
          <button
            onClick={toggle}
            className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold tracking-widest uppercase text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div
          className="rounded-xl border border-border bg-surface p-8"
          style={{ boxShadow: "var(--shadow-lift)" }}
        >
          <p className="eyebrow text-primary">{t.tagline}</p>
          <h1 className="mt-3 text-2xl font-semibold">
            {mode === "in" ? t.signInTitle : t.signUpTitle}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {mode === "in" ? t.signInLead : t.signUpLead}
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-3">
            {mode === "up" && (
              <>
                <input
                  className={inputClass}
                  placeholder={t.fullName}
                  value={form.fullName}
                  onChange={set("fullName")}
                  maxLength={120}
                  required
                />
                <input
                  className={inputClass}
                  placeholder={`${t.country} — ${t.countryPlaceholder}`}
                  value={form.country}
                  onChange={set("country")}
                  maxLength={80}
                  required
                />
                <input
                  className={inputClass}
                  placeholder={`${t.medicalArea} — ${t.medicalAreaPlaceholder}`}
                  value={form.medicalArea}
                  onChange={set("medicalArea")}
                  maxLength={120}
                  required
                />
              </>
            )}
            <input
              className={inputClass}
              type="email"
              placeholder={t.email}
              value={form.email}
              onChange={set("email")}
              maxLength={255}
              required
            />
            <input
              className={inputClass}
              type="password"
              placeholder={t.password}
              value={form.password}
              onChange={set("password")}
              minLength={8}
              maxLength={72}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy ? t.loading : mode === "in" ? t.signIn : t.signUp}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {t.or}
            <span className="h-px flex-1 bg-border" />
          </div>

          <button
            onClick={onGoogle}
            disabled={busy}
            className="w-full rounded-full border border-border px-4 py-2.5 text-sm font-semibold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
          >
            {t.google}
          </button>

          <button
            onClick={() => setMode((m) => (m === "in" ? "up" : "in"))}
            className="mt-6 w-full text-center text-xs text-muted-foreground transition-colors hover:text-primary"
          >
            {mode === "in" ? t.toSignUp : t.toSignIn}
          </button>
        </div>
      </div>
    </main>
  );
}
